import { Card } from '@iheartjane/reefer';
import type { SpecialType } from '@iheartjane/types';
import { useCallback, useEffect, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';

import { useConditionSections } from '@jane/business-admin/components/shared/specials/modal/multipleConditions/useConditionSections';
import { useRetailOrgSettings } from '@jane/business-admin/hooks/useRetailOrgSettings';
import { useStoreId } from '@jane/business-admin/hooks/useStoreId';
import { useGlobalSpecialsModal } from '@jane/business-admin/providers/GlobalSpecialsModalProvider';
import { useSpecialsModal } from '@jane/business-admin/providers/SpecialsModalProvider';
import type { RulesEntities, SpecialRulesV2 } from '@jane/business-admin/types';
import { EventNames, MODAL_CARD_WIDTH, track } from '@jane/business-admin/util';
import type {
  ConditionApplyToOption,
  ConditionEditorKey,
  ConditionSetsByKey,
} from '@jane/shared-b2b/components';
import {
  addConditionSet,
  ConditionsEditor,
  deselectConditionSetSection,
  duplicateConditionSet,
  getConditionEditors,
  removeConditionSet,
  updateConditionSetApplyTo,
  updateConditionSetSelection,
  validateConditionSets,
} from '@jane/shared-b2b/components';

import type { SpecialFormDisplay } from '../form';
import { getConditionGroupCount } from '../form';
import { DiscountedFields } from './discountedFields';
import { ProductsModal } from './productsModal';
import { RequiredFields } from './requiredFields';

/** The form fields the conditions editors read and write */
interface ConditionsForm {
  conditionSets: ConditionSetsByKey;
  display: SpecialFormDisplay;
  rules_entities?: RulesEntities;
}

interface Props {
  /** Collapse the saved groups, as when editing an existing special */
  defaultCollapsed?: boolean;
  specialType?: SpecialType | '';
}

interface ConditionsWrapperProps {
  conditionFor: string;
  defaultCollapsed?: boolean;
  editorIndex: number;
  hideConditions?: boolean;
  rulesEntities?: RulesEntities;
  rulesKey: ConditionEditorKey;
  specialType?: SpecialType | '';
}

const ConditionsWrapper = ({
  conditionFor,
  defaultCollapsed,
  editorIndex,
  hideConditions,
  rulesKey,
  rulesEntities,
  specialType,
}: ConditionsWrapperProps) => {
  const {
    posSyncMap: { isJanePosSynced, posSynced },
    isReadOnly,
  } = useSpecialsModal();
  const { isDisabled: globalSpecialDisabled } = useGlobalSpecialsModal();
  const isAnyPosSynced = isJanePosSynced || posSynced;
  const disabled = Boolean(
    isAnyPosSynced || isReadOnly || globalSpecialDisabled
  );
  const { control, getValues } = useFormContext<ConditionsForm>();
  const { special_id: specialId } = useParams<'special_id'>();
  /**
   * The sections are built from the groups below, so the rule that validates a
   * group cannot close over them. A ref lets the rule read the sections as they
   * are now rather than the ones it was registered with on the first render.
   */
  const applyToOptionsRef = useRef<ConditionApplyToOption[]>([]);

  const { field } = useController({
    control,
    name: `conditionSets.${rulesKey}`,
    rules: {
      validate: (conditionSets) =>
        validateConditionSets(conditionSets, applyToOptionsRef.current) ?? true,
    },
  });
  const conditionSets = field.value;

  const { orgId } = useRetailOrgSettings();
  const storeId = useStoreId();
  // No store id means this special is org-wide rather than per-store
  const isGlobal = storeId === '';

  /** Memoized — `useConditionSections` rebuilds every section when this changes */
  const renderProductsModal = useCallback(
    () => <ProductsModal isGlobal={isGlobal} orgId={orgId} storeId={storeId} />,
    [isGlobal, orgId, storeId]
  );

  const applyToOptions = useConditionSections({
    conditionSets,
    isGlobal,
    knownBrands: rulesEntities?.brands,
    orgId,
    renderProductsModal,
    specialType,
    storeId,
  });

  useEffect(() => {
    applyToOptionsRef.current = applyToOptions;
  }, [applyToOptions]);

  /** Every edit is the user's, so each one dirties the form */
  const updateConditionSets = (
    update: (current: typeof conditionSets) => typeof conditionSets
  ) => field.onChange(update(conditionSets));

  return (
    <Card border="grays-light" width={MODAL_CARD_WIDTH} mb={32}>
      <ConditionsEditor
        applyToOptions={applyToOptions}
        editorIndex={editorIndex}
        conditionFor={conditionFor}
        conditionSets={conditionSets}
        defaultCollapsed={defaultCollapsed}
        disabled={disabled}
        headerFields={
          conditionFor.includes('required') ? (
            <RequiredFields disabled={disabled} />
          ) : conditionFor.includes('discounted') ? (
            <DiscountedFields disabled={disabled} />
          ) : undefined
        }
        hideConditions={hideConditions}
        onAddGroup={() => {
          const nextConditionSets = addConditionSet(
            conditionSets,
            'attributes'
          );
          field.onChange(nextConditionSets);
          track({
            condition_group_count: getConditionGroupCount({
              conditionSets: {
                ...getValues('conditionSets'),
                [rulesKey]: nextConditionSets,
              },
              display: getValues('display'),
              includeEmpty: true,
              specialType,
            }),
            event: EventNames.SpecialConditionGroupAdded,
            special_id: specialId === 'create' ? null : specialId || null,
            store_id: storeId || null,
          });
        }}
        onApplyToChange={(index, value) =>
          updateConditionSets((current) =>
            updateConditionSetApplyTo(
              current,
              index,
              value,
              applyToOptions.find((option) => option.value === value)
                ?.conditions ?? []
            )
          )
        }
        onChangeGroup={(index, selectionType, key, value) =>
          updateConditionSets((current) =>
            updateConditionSetSelection(
              current,
              index,
              selectionType,
              key,
              value
            )
          )
        }
        onDeleteGroup={(index) =>
          updateConditionSets((current) => removeConditionSet(current, index))
        }
        onDeselectGroup={(index, key) =>
          updateConditionSets((current) =>
            deselectConditionSetSection(current, index, key)
          )
        }
        onDuplicateGroup={(index) =>
          updateConditionSets((current) =>
            duplicateConditionSet(current, index)
          )
        }
        variant="multiple"
      />
    </Card>
  );
};

export const MultipleConditions = ({
  defaultCollapsed,
  specialType,
}: Props) => {
  const { watch } = useFormContext<{
    display: SpecialFormDisplay;
    rules: SpecialRulesV2;
    rules_entities?: RulesEntities;
  }>();
  // Names for the brands and products the rules reference, sent with the
  // special rather than fetched, so a tag never waits on a picker's page
  const rulesEntities = watch('rules_entities');
  const hideDiscountedConditions =
    ['bundle', 'spending_threshold'].includes(specialType || '') &&
    watch(
      `display.${specialType === 'spending_threshold' ? 'spending_threshold' : 'bundle.settings'}.copy_conditions_for_discounted_products`
    ) === true;

  if (specialType === 'qualified_group') {
    return null;
  }

  return getConditionEditors(specialType).map(
    ({ conditionFor, rulesKey }, index) => (
      <ConditionsWrapper
        key={rulesKey}
        editorIndex={index}
        conditionFor={conditionFor}
        defaultCollapsed={defaultCollapsed}
        rulesEntities={rulesEntities}
        rulesKey={rulesKey}
        specialType={specialType}
        hideConditions={
          conditionFor.includes('discounted') && hideDiscountedConditions
        }
      />
    )
  );
};
